import React, { useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import Chart from 'chart.js/auto';
import { Sparkles, Palette, Compass, ScrollText } from 'lucide-react';
import logoSvg from '@/assets/images/nexusprime.svg';
import logoJpg from '@/assets/images/nexusprime.jpg';

export default function AboutPage({ onBack }) {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [engineMode, setEngineMode] = useState('eye');
  const [selectedPillar, setSelectedPillar] = useState(0);
  const [activeSection, setActiveSection] = useState('');

  const activeButtonClass = "text-violet-700";
  const navButtonClass = "text-stone-500 hover:text-violet-700 transition-colors p-1";
  const navLogoClass = "w-8 h-8 rounded-full bg-white shadow-sm border border-stone-200 hover:shadow-md transition-shadow flex items-center justify-center p-1.5";

  // Array de pilares com traduções
  const pillars = [
    { label: t('about.pillar1.title'), desc: t('about.pillar1.desc'), color: '#4c1d95' },
    { label: t('about.pillar2.title'), desc: t('about.pillar2.desc'), color: '#059669' },
    { label: t('about.pillar3.title'), desc: t('about.pillar3.desc'), color: '#0d9488' },
    { label: t('about.pillar4.title'), desc: t('about.pillar4.desc'), color: '#0284c7' },
    { label: t('about.pillar5.title'), desc: t('about.pillar5.desc'), color: '#2563eb' },
    { label: t('about.pillar6.title'), desc: t('about.pillar6.desc'), color: '#7c3aed' }
  ];

  // Intersection Observer para destacar a seção visível
  useEffect(() => {
    const sections = ['top', 'genesis', 'symbol', 'experience', 'manifesto'];
    const observers = sections.map(id => {
      const element = document.getElementById(id);
      if (!element) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(id);
          }
        },
        { threshold: 0.3 } // 30% da seção visível
      );
      observer.observe(element);
      return observer;
    });

    return () => observers.forEach(obs => obs?.disconnect());
  }, []);

  // Configuração do gráfico radar
  useEffect(() => {
    if (chartRef.current) {
      const ctx = chartRef.current.getContext('2d');
      if (chartInstance.current) chartInstance.current.destroy();

      const gradient = ctx.createLinearGradient(0, 0, 0, 400);
      gradient.addColorStop(0, 'rgba(76, 29, 149, 0.4)');
      gradient.addColorStop(1, 'rgba(21, 128, 61, 0.4)');

      chartInstance.current = new Chart(ctx, {
        type: 'radar',
        data: {
          labels: pillars.map(p => p.label),
          datasets: [{
            label: 'Pillar Strength',
            data: [10, 10, 10, 10, 10, 10],
            backgroundColor: gradient,
            borderColor: '#4c1d95',
            borderWidth: 2,
            pointBackgroundColor: '#fff',
            pointBorderColor: '#4c1d95',
            pointHoverBackgroundColor: '#15803d',
            pointHoverRadius: 8,
            pointHitRadius: 20,
            pointRadius: 5
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            r: {
              beginAtZero: true,
              max: 10,
              ticks: { display: false },
              pointLabels: {
                font: { family: "'Space Grotesk', sans-serif", size: 13, weight: 'bold' },
                color: '#44403c'
              },
              grid: { color: '#e7e5e4' },
              angleLines: { color: '#d6d3d1' }
            }
          },
          plugins: { legend: { display: false }, tooltip: { enabled: false } },
          onClick: (evt, elements) => {
            if (elements.length > 0) setSelectedPillar(elements[0].index);
          }
        }
      });
    }
    return () => {
      if (chartInstance.current) chartInstance.current.destroy();
    };
  }, [pillars]);

  // Função para avançar para o próximo pilar
  const handlePillarClick = () => {
    setSelectedPillar((prev) => (prev + 1) % pillars.length);
  };

  // Rolagem suave para uma seção específica
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80; // altura da navbar fixa
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      {/* Barra de navegação fixa */}
      <nav className="sticky top-0 z-40 bg-stone-50/95 backdrop-blur-sm border-b border-stone-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 rounded-full hover:bg-stone-200 transition"
                aria-label={t('common.back')}
              >
                <ArrowLeft size={20} />
              </button>
              <span className="brand-font font-bold text-xl tracking-tight text-stone-900"></span>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 justify-end">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`${navLogoClass} ${activeSection === 'top' ? activeButtonClass : ''}`}
                title="Voltar ao topo"
              >
                <img src={logoSvg} alt="Nexusprime" className="w-5 h-5" />
              </button>

              <button
                onClick={() => scrollToSection('genesis')}
                className={`${navButtonClass} ${activeSection === 'genesis' ? activeButtonClass : ''}`}
                title={t('about.genesisMenu')}
              >
                <Sparkles size={18} />
              </button>

              <button
                onClick={() => scrollToSection('symbol')}
                className={`${navButtonClass} ${activeSection === 'symbol' ? activeButtonClass : ''}`}
                title={t('about.symbolMenu')}
              >
                <Palette size={18} />
              </button>

              <button
                onClick={() => scrollToSection('experience')}
                className={`${navButtonClass} ${activeSection === 'experience' ? activeButtonClass : ''}`}
                title={t('about.experienceMenu')}
              >
                <Compass size={18} />
              </button>

              <button
                onClick={() => scrollToSection('manifesto')}
                className={`${navButtonClass} ${activeSection === 'manifesto' ? activeButtonClass : ''}`}
                title={t('about.manifestoMenu')}
              >
                <ScrollText size={18} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div id="top" style={{ position: 'absolute', top: 0, height: 1, opacity: 0 }}></div>

      {/* Hero Text */}
      <header className="bg-stone-50 pt-16 pb-12 lg:pt-24 border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-stone-900 mb-6 leading-tight">
            <Trans
              i18nKey="about.heroTitle"
              components={{
                span1: <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-green-500" />,
                span2: <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-700 to-violet-500" />
              }}
            />
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            {t('about.heroSubtitle')}
          </p>
        </div>
      </header>

      {/* Hero Image */}
      <div className="w-full h-48 md:h-64 overflow-hidden bg-white flex items-center justify-center py-12">
        <img
          src={logoJpg}
          alt="Nexusprime Hero"
          className="w-full h-full object-contain"
        />
      </div>

      <main className="flex-grow">
        {/* Gênese */}
        <section id="genesis" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center mb-16">
              <h2 className="text-3xl font-bold text-stone-900 mb-4">{t('about.genesisTitle')}</h2>
              <p className="text-stone-600">{t('about.genesisSubtitle')}</p>
            </div>
            <div className="prose prose-stone max-w-4xl mx-auto mb-12">
              <p className="text-stone-700 leading-relaxed">{t('about.genesisParagraph')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="group relative bg-stone-50 rounded-2xl p-10 border border-stone-200 hover:border-violet-300 hover:shadow-xl transition-all text-center">
                <div className="absolute top-0 left-0 w-full h-1 bg-violet-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                <h3 className="text-5xl font-bold text-violet-900 mb-2 brand-font">{t('about.nexusCard.title')}</h3>
                <p className="text-sm text-violet-500 uppercase tracking-widest mb-6 font-semibold">{t('about.nexusCard.origin')}</p>
                <p className="text-stone-800 text-xl font-medium leading-relaxed">{t('about.nexusCard.meaning')}</p>
              </div>
              <div className="group relative bg-stone-50 rounded-2xl p-10 border border-stone-200 hover:border-green-300 hover:shadow-xl transition-all text-center">
                <div className="absolute top-0 left-0 w-full h-1 bg-green-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                <h3 className="text-5xl font-bold text-green-900 mb-2 brand-font">{t('about.primeCard.title')}</h3>
                <p className="text-sm text-green-600 uppercase tracking-widest mb-6 font-semibold">{t('about.primeCard.origin')}</p>
                <p className="text-stone-800 text-xl font-medium leading-relaxed">{t('about.primeCard.meaning')}</p>
              </div>
            </div>
            <p className="text-center text-stone-700 mt-8 text-lg">
              <Trans i18nKey="about.genesisConclusion" components={{ strong: <strong /> }} />
            </p>
          </div>
        </section>

        {/* Hexágono + Radar Chart */}
        <section id="symbol" className="py-20 bg-stone-50 border-y border-stone-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5 space-y-8">
                <div>
                  <div className="inline-block px-3 py-1 text-xs font-semibold tracking-wider text-violet-700 uppercase bg-violet-100 rounded-full mb-4">
                    {t('about.symbolBadge')}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">{t('about.symbolTitle')}</h2>
                  <p className="text-stone-600 leading-relaxed">{t('about.symbolDesc')}</p>
                </div>
                <div
                  className="bg-white p-6 rounded-xl border-l-4 border-violet-600 shadow-sm min-h-[140px] cursor-pointer hover:shadow-md transition"
                  onClick={handlePillarClick}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => e.key === 'Enter' && handlePillarClick()}
                >
                  <h4 className="text-xl font-bold mb-2" style={{ color: pillars[selectedPillar].color }}>
                    {selectedPillar + 1}. {pillars[selectedPillar].label}
                  </h4>
                  <p className="text-stone-600">{pillars[selectedPillar].desc}</p>
                  <p className="text-xs text-stone-400 mt-4 italic">{t('about.pillarClickHint')}</p>
                </div>
              </div>
              <div className="lg:col-span-7 bg-white rounded-3xl p-6 shadow-sm border border-stone-100">
                <div className="relative w-full h-[400px]">
                  <canvas ref={chartRef} id="hexChart"></canvas>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Olho e Setas (Toggle) */}
        <section className="py-24 bg-stone-900 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-96 h-96 bg-violet-900 rounded-full blur-[100px] opacity-20"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-green-900 rounded-full blur-[100px] opacity-20"></div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">{t('about.heartTitle')}</h2>
              <p className="text-stone-400 max-w-2xl mx-auto">{t('about.heartSubtitle')}</p>
              <div className="mt-8 inline-flex bg-stone-800 p-1 rounded-xl shadow-lg border border-stone-700">
                <button
                  onClick={() => setEngineMode('eye')}
                  className={`px-8 py-3 rounded-lg text-sm font-bold tracking-wide transition-all ${
                    engineMode === 'eye'
                      ? 'bg-violet-700 text-white shadow-md'
                      : 'text-stone-400 hover:text-white'
                  }`}
                >
                  {t('about.eyeButton')}
                </button>
                <button
                  onClick={() => setEngineMode('arrows')}
                  className={`px-8 py-3 rounded-lg text-sm font-bold tracking-wide transition-all ${
                    engineMode === 'arrows'
                      ? 'bg-green-700 text-white shadow-md'
                      : 'text-stone-400 hover:text-white'
                  }`}
                >
                  {t('about.arrowsButton')}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
              {/* Visualização animada */}
              <div className="h-96 bg-stone-800/50 rounded-2xl border border-stone-700 flex items-center justify-center relative overflow-hidden backdrop-blur-sm">
                {engineMode === 'eye' ? (
                  <div className="flex flex-col items-center transition-all duration-500">
                    <div className="relative w-32 h-32 mb-8">
                      <div className="absolute inset-0 rounded-full border-4 border-violet-500 animate-pulse"></div>
                      <div className="absolute inset-2 rounded-full border-2 border-dashed border-violet-300 animate-spin-slow"></div>
                      <div className="absolute inset-4 rounded-full bg-gradient-to-br from-violet-600 to-violet-800 flex items-center justify-center">
                        <div className="w-4 h-4 bg-white rounded-full shadow-lg"></div>
                      </div>
                    </div>
                    <div className="text-center">
                      <h3 className="text-violet-300 font-bold text-lg tracking-wider mb-1">OMNISIGHT</h3>
                      <span className="text-xs text-stone-500 uppercase tracking-widest">{t('about.eyeContentTitle')}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center transition-all duration-500">
                    <div className="relative w-32 h-32 mb-8">
                      <div className="absolute inset-0 rounded-full border-8 border-transparent border-t-green-700 border-r-green-300 animate-spin"></div>
                      <div className="absolute inset-4 rounded-full border-4 border-transparent border-b-green-700 border-l-green-300 animate-spin-reverse"></div>
                      <div className="absolute inset-10 rounded-full bg-green-500/20 blur-sm"></div>
                    </div>
                    <div className="text-center">
                      <h3 className="text-green-300 font-bold text-lg tracking-wider mb-1">FLUXO ETERNO</h3>
                      <span className="text-xs text-stone-500 uppercase tracking-widest">{t('about.arrowsContentTitle')}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Conteúdo descritivo */}
              <div>
                {engineMode === 'eye' ? (
                  <div className="animate-fade-in">
                    <h3 className="text-3xl font-bold text-white mb-6">{t('about.eyeContentTitle')}</h3>
                    <p className="text-stone-300 text-lg leading-relaxed mb-6">
                      <Trans i18nKey="about.eyeContentDesc" components={{ strong: <strong />, em: <em /> }} />
                    </p>
                    <ul className="space-y-4">
                      <li className="flex items-start"><span className="text-violet-300 mr-3">✓</span>{t('about.eyeItem1')}</li>
                      <li className="flex items-start"><span className="text-violet-300 mr-3">✓</span>{t('about.eyeItem2')}</li>
                      <li className="flex items-start"><span className="text-violet-300 mr-3">✓</span>{t('about.eyeItem3')}</li>
                    </ul>
                  </div>
                ) : (
                  <div className="animate-fade-in">
                    <h3 className="text-3xl font-bold text-white mb-6">{t('about.arrowsContentTitle')}</h3>
                    <p className="text-stone-300 text-lg leading-relaxed mb-6">{t('about.arrowsContentDesc')}</p>
                    <div className="space-y-4">
                      <div className="bg-stone-800 p-4 rounded-lg border-l-4 border-green-500">
                        <h4 className="text-green-400 font-bold mb-1">{t('about.arrowsCard1.title')}</h4>
                        <p className="text-stone-400 text-sm">{t('about.arrowsCard1.desc')}</p>
                      </div>
                      <div className="bg-stone-800 p-4 rounded-lg border-l-4 border-green-500">
                        <h4 className="text-green-400 font-bold mb-1">{t('about.arrowsCard2.title')}</h4>
                        <p className="text-stone-400 text-sm">{t('about.arrowsCard2.desc')}</p>
                      </div>
                      <div className="bg-stone-800 p-4 rounded-lg border-l-4 border-green-500">
                        <h4 className="text-green-400 font-bold mb-1">{t('about.arrowsCard3.title')}</h4>
                        <p className="text-stone-400 text-sm">{t('about.arrowsCard3.desc')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Circuitos */}
        <section className="py-12 bg-white border-y border-stone-200">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h3 className="text-2xl font-bold text-stone-900 mb-4">{t('about.circuitsTitle')}</h3>
            <p className="text-stone-600 leading-relaxed">
              <Trans i18nKey="about.circuitsDesc" components={{ em: <em /> }} />
            </p>
          </div>
        </section>

        {/* Experiência */}
        <section id="experience" className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-stone-900">{t('about.experienceTitle')}</h2>
              <p className="text-stone-600 mt-2">{t('about.experienceSubtitle')}</p>
            </div>

            <div className="relative pl-4 sm:pl-0">
              {[
                { number: 1, titleKey: 'step1Title', descKey: 'step1Desc' },
                { number: 2, titleKey: 'step2Title', descKey: 'step2Desc' },
                { number: 3, titleKey: 'step3Title', descKey: 'step3Desc' }
              ].map((step, idx) => (
                <div key={idx} className="relative sm:pl-32 pb-16 group">
                  {idx < 2 && <div className="absolute left-[23px] sm:left-[119px] top-12 bottom-0 w-0.5 bg-stone-300"></div>}
                  <div className="absolute left-0 sm:left-[96px] top-0 w-12 h-12 rounded-full bg-stone-100 border-2 border-stone-300 flex items-center justify-center text-stone-500 font-bold z-10 group-hover:bg-violet-600 group-hover:text-white group-hover:border-violet-600 transition-all">
                    {step.number}
                  </div>
                  <div className="pl-16 sm:pl-8">
                    <div className="bg-stone-50 p-6 rounded-xl border border-stone-100 hover:border-violet-200 hover:shadow-md transition-all">
                      <h4 className="text-xl font-bold text-stone-900 mb-2">{t(`about.${step.titleKey}`)}</h4>
                      <p className="text-stone-600">{t(`about.${step.descKey}`)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Manifesto */}
        <section id="manifesto" className="py-16 bg-stone-100 border-t border-stone-200 text-center">
          <div className="max-w-3xl mx-auto px-4">
            <div className="relative max-w-3xl mx-auto px-6">
              {/* Aspa superior esquerda */}
              <div className="absolute -top-6 left-0 text-6xl text-violet-300 font-serif">
                ❝
              </div>

              <blockquote className="text-2xl md:text-3xl font-serif text-stone-800 italic leading-relaxed mb-10 whitespace-pre-line text-center">
                {t('about.manifesto')}
              </blockquote>

              {/* Aspa inferior direita */}
              <div className="absolute -bottom-12 right-0 text-6xl text-violet-300 font-serif">
                ❞
              </div>
            </div>

            <br />
            <div className="h-1 w-20 bg-gradient-to-r from-violet-600 to-green-600 mx-auto rounded-full mb-8"></div>
            <div className="text-center">
              <p className="text-stone-500 font-bold text-lg uppercase tracking-wider">
                {t('about.finalPhrase')}
              </p>
              <p className="text-stone-800 font-bold mt-2 text-lg">
                {t('about.finalWelcome')}
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}